import { useEffect, useRef } from 'react';

interface UseSwipeToOpenMenuOptions {
  onOpen: () => void;
  onClose: () => void;
  isOpen: boolean;
  enabled?: boolean;
  edgeThreshold?: number; // Distance from left/right edge to trigger (default: 50px)
  minSwipeDistance?: number; // Minimum swipe distance to trigger (default: 100px)
}

/**
 * Hook to enable swipe gestures to open/close mobile menu
 * - Swipe right from left edge: Opens menu
 * - Swipe left from anywhere when menu is open: Closes menu
 * Only triggers on mobile devices and avoids conflicts with elements that have their own swipe handlers
 */
export function useSwipeToOpenMenu({
  onOpen,
  onClose,
  isOpen,
  enabled = true,
  edgeThreshold = 50,
  minSwipeDistance = 100,
}: UseSwipeToOpenMenuOptions) {
  const touchStartX = useRef<number | null>(null);
  const touchStartY = useRef<number | null>(null);
  const touchStartTime = useRef<number | null>(null);
  const elementWithSwipe = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!enabled) return;

    // Only enable on mobile (touch devices)
    const isMobile = window.matchMedia('(max-width: 1024px)').matches && 
                     'ontouchstart' in window;

    if (!isMobile) return;

    const handleTouchStart = (e: TouchEvent) => {
      const touch = e.touches[0];
      touchStartX.current = touch.clientX;
      touchStartY.current = touch.clientY;
      touchStartTime.current = Date.now();

      // Check if the touch started near the left edge (for opening) or anywhere when menu is open (for closing)
      if (isOpen) {
        // When menu is open, allow swipe from anywhere to close
        const target = e.target as HTMLElement;
        elementWithSwipe.current = findElementWithSwipeHandlers(target);
      } else if (touch.clientX <= edgeThreshold) {
        // When menu is closed, only allow swipe from left edge to open
        const target = e.target as HTMLElement;
        elementWithSwipe.current = findElementWithSwipeHandlers(target);
      } else {
        // Touch didn't start from edge and menu is closed, ignore
        touchStartX.current = null;
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      // Only interfere if we're sure it's a menu swipe gesture
      if (touchStartX.current !== null && !elementWithSwipe.current) {
        const touch = e.touches[0];
        const deltaX = touch.clientX - touchStartX.current;
        const deltaY = Math.abs(touch.clientY - (touchStartY.current || 0));
        const absDeltaX = Math.abs(deltaX);

        // If user is scrolling vertically, don't interfere
        if (deltaY > absDeltaX * 1.5) {
          // This is primarily a vertical scroll, allow it
          return;
        }

        if (isOpen) {
          // When menu is open, prevent default on left swipe (to close)
          if (deltaX < -50 && absDeltaX > deltaY * 2) {
            e.preventDefault();
          }
        } else {
          // When menu is closed, prevent default on right swipe from edge (to open)
          // Only if it's clearly horizontal and started from edge
          if (touchStartX.current <= edgeThreshold && deltaX > 50 && absDeltaX > deltaY * 2) {
            e.preventDefault();
          }
        }
      }
    };

    const handleTouchEnd = (e: TouchEvent) => {
      if (touchStartX.current === null || touchStartY.current === null) return;

      const touch = e.changedTouches[0];
      const deltaX = touch.clientX - touchStartX.current;
      const deltaY = Math.abs(touch.clientY - touchStartY.current);
      const deltaTime = touchStartTime.current ? Date.now() - touchStartTime.current : 0;
      const absDeltaX = Math.abs(deltaX);

      // Check if this was a valid horizontal swipe
      const isHorizontalSwipe = absDeltaX > deltaY;
      const isNotTooSlow = deltaTime < 500; // Swipe should be quick (< 500ms)
      const hasMinimumDistance = absDeltaX > minSwipeDistance;

      if (!isHorizontalSwipe || !hasMinimumDistance || !isNotTooSlow || elementWithSwipe.current) {
        // Reset and exit if conditions not met
        touchStartX.current = null;
        touchStartY.current = null;
        touchStartTime.current = null;
        elementWithSwipe.current = null;
        return;
      }

      if (isOpen) {
        // Menu is open: swipe left to close
        if (deltaX < -minSwipeDistance) {
          onClose();
        }
      } else {
        // Menu is closed: swipe right from left edge to open
        const startedFromEdge = touchStartX.current <= edgeThreshold;
        if (startedFromEdge && deltaX > minSwipeDistance) {
          onOpen();
        }
      }

      // Reset
      touchStartX.current = null;
      touchStartY.current = null;
      touchStartTime.current = null;
      elementWithSwipe.current = null;
    };

    // Add listeners to document for global swipe detection
    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [enabled, edgeThreshold, minSwipeDistance, onOpen, onClose, isOpen]);
}

/**
 * Recursively checks if an element or its parents have swipe/touch handlers
 * to avoid conflicts with existing swipe functionality
 */
function findElementWithSwipeHandlers(element: HTMLElement | null): HTMLElement | null {
  if (!element) return null;

  // Check for Swiper.js components (common carousel library)
  const swiperElement = element.closest('.swiper, .swiper-wrapper, .swiper-slide');
  if (swiperElement) {
    return swiperElement as HTMLElement;
  }

  // Check for common swipeable component patterns
  // Look for elements that have data attributes indicating swipe functionality
  let current: HTMLElement | null = element;
  let depth = 0;
  const maxDepth = 8; // Increased depth to catch nested components

  while (current && depth < maxDepth) {
    // Check for explicit swipe markers
    if (current.dataset.swipeable === 'true' || 
        current.dataset.swipe === 'true' ||
        current.hasAttribute('data-swipe') ||
        current.hasAttribute('data-swipeable')) {
      return current;
    }

    // Check for elements with touch event handlers by looking for specific classes
    // that are commonly used with swipe handlers
    const className = current.className || '';
    if (typeof className === 'string') {
      // Look for patterns that suggest interactive/swipeable content
      if (className.includes('garden-planner') || 
          className.includes('tab-content') ||
          className.includes('listing-type-tabs') ||
          className.includes('swipeable') ||
          className.includes('carousel')) {
        return current;
      }
    }

    // Check if we're inside a horizontal scrollable container (might be swipeable)
    const styles = window.getComputedStyle(current);
    const isHorizontalScroll = styles.overflowX === 'auto' || styles.overflowX === 'scroll';
    const isScrollContainer = isHorizontalScroll && current.scrollWidth > current.clientWidth;
    
    // If it's a horizontal scroll container and relatively small, it might be swipeable
    if (isScrollContainer && current.clientWidth < window.innerWidth * 0.9) {
      return current;
    }

    // Check for specific components we know have swipe handlers
    // FieldDetails tabs, ListingTypeTabs, etc.
    if (current.id) {
      const id = current.id.toLowerCase();
      if (id.includes('tab') || id.includes('swipe') || id.includes('carousel')) {
        return current;
      }
    }

    current = current.parentElement;
    depth++;
  }

  return null;
}

